/**
 * 블로그 중복 글 301 (데이터 기반 — 응답 slug ≠ URL slug).
 *
 * 영구지침: docs/develop/seo/ssr-spec.md §16.4.1
 *
 * 백엔드는 옛(리디렉트된) slug 요청에 대표 글을 200 으로 주고, 응답 `slug` 는 대표 글 slug 다.
 * BlogDetailPage 는 모든 훅 호출 뒤 이 모듈로 판정해 SSR 301 / CSR `<Navigate replace>` 로 보낸다.
 */
import { createElement, type ReactElement } from 'react'
import { Navigate } from 'react-router-dom'
import type { SsrStatus } from '@/hooks/useSsrStatus'

/** `decodeURIComponent` 실패(잘못된 `%` 시퀀스) 시 원문 유지 + NFC 정규화 */
function normalizeSlug(slug: string): string {
  let decoded = slug
  try {
    decoded = decodeURIComponent(slug)
  } catch {
    // 디코딩 불가 — 원문 그대로 비교
  }
  return decoded.normalize('NFC')
}

/**
 * 301 대상 경로를 계산한다. 리디렉트가 필요 없으면 `null`.
 *
 * @param urlSlug  라우트 파라미터(`useParams` — 디코딩된 값)
 * @param postSlug 백엔드 응답의 slug (대표 글 slug)
 * @param search   현재 쿼리스트링(`?utm_source=…` 또는 빈 문자열) — 그대로 보존
 */
export function resolveBlogSlugRedirect(
  urlSlug: string | undefined,
  postSlug: string | null | undefined,
  search: string,
): string | null {
  if (!urlSlug || !postSlug) return null
  const target = normalizeSlug(postSlug)
  if (!target) return null
  // 인코딩·유니코드 정규화 차이만 있는 경우는 같은 글 — 리디렉트 루프 방지
  if (normalizeSlug(urlSlug) === target) return null
  return `/blog/${encodeURIComponent(target)}${search}`
}

/**
 * SSR: 301 + Location 을 세팅하고 `null` (HTML 은 버려진다. StaticRouter 에서 `<Navigate>` 는 무동작).
 * CSR: `<Navigate replace>` — 히스토리에 옛 URL 을 남기지 않는다.
 */
export function renderBlogSlugRedirect(
  target: string,
  ssrStatus: SsrStatus | null,
): ReactElement | null {
  if (ssrStatus) {
    ssrStatus.status = 301
    ssrStatus.redirect = target
    return null
  }
  return createElement(Navigate, { to: target, replace: true })
}
